import type { InventoryListByIdAPI } from '@openx/types/inventoryList.ts';
import {
  type AppInventoryOptionType,
  type AppInventoryState,
  AppInventoryVersion,
} from '@openx/types/targeting/appInventory.ts';
import { ComparisonType } from '@openx/types/targeting/targetingValuesTypes.ts';
import { noop } from '@openx/utils/noop.ts';
import { type Dispatch, type SetStateAction, useState } from 'react';
import { useTargetingContext } from '../../../../utils/context/TargetingContext.tsx';
import type { AppInventoryProps } from '../../../InventoryAndContent/InventoryAndContentTopBox/constants.ts';
import { BulkDrawer } from '../../../InventoryAndContent/InventoryAndContentTopBox/partials/BulkDrawer.tsx';
import type { InvalidItemsError } from '../../../shared/BulkAdd/types.ts';
import { useBulkAdd } from '../../../shared/BulkAdd/useBulkAdd.ts';
import { TopBoxRenderer } from '../../../shared/InventoryAndContent/components/TopBoxRenderer.tsx';
import { INVENTORY_LIMITS } from '../../../shared/InventoryAndContent/constants.ts';
import { useAppInventoryTopBox } from '../../../shared/InventoryAndContent/hooks/useAppInventoryTopBox.ts';
import type { TargetingItemsProps } from '../../../types.ts';
import { useGetDrawerContent } from '../../hooks/useGetDrawerContent.tsx';
import { comparisonOptions, optionsWithBulkAdd } from './constants.ts';
import { useSetupAppInventory } from './hooks/useSetupAppInventory.ts';

type InventoryTopboxProps = {
  setAppBundleIdDuplicateErrors: Dispatch<SetStateAction<InvalidItemsError | null>>;
  inventoryListItems?: InventoryListByIdAPI['values'];
  contentOption: AppInventoryOptionType;
  setContentOption: Dispatch<SetStateAction<AppInventoryOptionType>>;
} & AppInventoryProps &
  TargetingItemsProps<AppInventoryState>;

export const AppInventoryTopBox = ({
  targetingParams,
  dispatchInventory = noop,
  accountUid = '',
  setAppBundleIdDuplicateErrors,
  inventoryListItems = [],
  displayContext,
  contentOption,
  setContentOption,
  appInventoryType: appInventoryTypeProp,
  setAppInventoryType: setAppInventoryTypeProp,
  appNameComparisonType: appNameComparisonTypeProp,
  setAppNameComparisonType: setAppNameComparisonTypeProp,
  bundleIdComparisonType: bundleIdComparisonTypeProp,
  setBundleIdComparisonType: setBundleIdComparisonTypeProp,
}: InventoryTopboxProps) => {
  const [comparisonType, setComparisonType] = useState<ComparisonType>(ComparisonType.EQUALS);

  // App Name Bulk Validation backed by appGroupSearch (always on; safe default if context hook is missing)
  const { useAppNameBulkValidationV2 } = useTargetingContext();
  const useValidationHook =
    useAppNameBulkValidationV2 ??
    (() => ({
      validateAppNames: async () => ({ errors: [], validItems: [] }),
      isReady: false,
    }));
  const { validateAppNames: appNameValidationV2, isReady: isValidationReady } = useValidationHook();

  const {
    effectiveContentOption,
    effectiveComparisonType,
    publisherInventoryRuleOptions,
    setPublisherInventoryRuleOptions,
    appInventoryType,
    setAppInventoryType,
    appNameComparisonType,
    bundleIdComparisonType,
  } = useAppInventoryTopBox<AppInventoryOptionType>({
    comparisonType,
    contentOption,
    appInventoryTypeProp,
    setAppInventoryTypeProp,
    appNameComparisonTypeProp,
    setAppNameComparisonTypeProp,
    bundleIdComparisonTypeProp,
    setBundleIdComparisonTypeProp,
  });

  const { addItems, itemsCount, comparisonFieldCriteria } = useSetupAppInventory({
    targetingParams,
    contentOption: effectiveContentOption, // Use effectiveContentOption to respect App Inventory type selection
    comparisonType: effectiveComparisonType, // Use effectiveComparisonType to respect App Inventory type selection
    dispatchInventory,
  });

  const { isBulkOpen, onBulkAdd, onBulkClose, onBulkOpen } = useBulkAdd({ onAdd: addItems });

  const isLimitReached = itemsCount >= INVENTORY_LIMITS[effectiveContentOption];
  const showBulkAdd = (optionsWithBulkAdd as AppInventoryOptionType[]).includes(
    effectiveContentOption as AppInventoryOptionType
  );

  const { drawerContent: drawerContentBase } = useGetDrawerContent({
    dispatchInventory,
    targetingParams,
    contentOption,
    comparisonType,
    setComparisonType,
    // App Inventory specific props
    appInventoryType,
    setAppInventoryType,
    appNameComparisonType,
    bundleIdComparisonType,
    accountUid,
    setAppBundleIdDuplicateErrors,
    inventoryVersion: AppInventoryVersion.APP_INVENTORY_ONLY_V2,
  });

  const parentAccountFeatures = {
    isAdUnitTargetingEnabled: false,
    isSiteTargetingEnabled: false,
  };

  return (
    <>
      <TopBoxRenderer
        comparisonType={comparisonType}
        contentOption={contentOption}
        parentAccountFeatures={parentAccountFeatures}
        showBulkAdd={showBulkAdd}
        isLimitReached={isLimitReached}
        targetingParams={targetingParams}
        setContentOption={setContentOption}
        setComparisonType={setComparisonType}
        dispatchInventory={dispatchInventory}
        onBulkOpen={onBulkOpen}
        accountUid={accountUid}
        setAppBundleIdDuplicateErrors={setAppBundleIdDuplicateErrors}
        inventoryVersion={AppInventoryVersion.APP_INVENTORY_ONLY_V2}
        drawerContentBase={drawerContentBase}
        // App Inventory specific props
        appInventoryType={appInventoryType}
      />

      {isBulkOpen && effectiveComparisonType && (
        <BulkDrawer
          contentOption={contentOption}
          effectiveContentOption={effectiveContentOption}
          itemsCount={itemsCount}
          comparisonFieldCriteria={comparisonFieldCriteria}
          comparisonType={effectiveComparisonType}
          setContentOption={setContentOption}
          setComparisonType={setComparisonType}
          onBulkAdd={onBulkAdd}
          onBulkClose={onBulkClose}
          parentAccountFeatures={parentAccountFeatures}
          inventoryListItems={inventoryListItems}
          publisherInventoryRuleOptions={publisherInventoryRuleOptions}
          setPublisherInventoryRuleOptions={setPublisherInventoryRuleOptions}
          targetingParams={targetingParams}
          displayContext={displayContext}
          appInventoryType={appInventoryType}
          appNameValidationV2={isValidationReady ? appNameValidationV2 : undefined}
          inventoryVersion={AppInventoryVersion.APP_INVENTORY_ONLY_V2}
          customComparisonOptions={comparisonOptions}
        />
      )}
    </>
  );
};
